import { Component, EventEmitter, Output, Input, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule, ValidatorFn, AbstractControl, ValidationErrors } from '@angular/forms';
import { UserService } from '../../../service/user.service';
import { AuthService } from '../../../service/auth.service';
import { User } from '../models/user.interface';
import { LucideIconsModule } from '../../../shared/lucide-icons.module';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { PrimengImports } from '../../../shared/primeng-imports';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { InputPatternDirective } from '../../../directives/input-pattern.directive';
import { EncryptionService } from '../../../service/encryption.service';


@Component({
  selector: 'app-user-login',
  imports: [CommonModule, LucideIconsModule, ReactiveFormsModule, PrimengImports, FormsModule, InputPatternDirective],
  templateUrl: './user-login.component.html',
  styleUrl: './user-login.component.css',
  providers: [MessageService]
})
export class UserLoginComponent implements OnChanges {
  @Input() isLogin: boolean = true;
  @Output() onLogin = new EventEmitter<User>();
  @Output() onClose = new EventEmitter<void>();
  showPassword = false;
  showConfirmPassword = false;
  loading = false;
  signInForm: FormGroup;
  signUpForm: FormGroup;
  forgotPasswordForm: FormGroup;
  showForgotPasswordVisisble = false;

  // Live password criteria tracking for checklist UI
  passwordCriteria = {
    length: false,
    upper: false,
    lower: false,
    number: false,
    special: false,
  };

  investmentGoalOptions = [
    { value: '', label: 'Select your investment focus' },
    { value: 'real-estate', label: 'Real Estate Portfolio' },
    { value: 'clean-energy', label: 'Clean Energy (LNG)' },
    { value: 'ai-tech', label: 'Artificial Intelligence' },
    { value: 'agriculture', label: 'Agriculture Fund' },
    { value: 'diversified', label: 'Diversified Portfolio' }
  ];

  constructor(
    private formBuilder: FormBuilder,
    private userService: UserService,
    private authService: AuthService,
    private router: Router,
    private messageService: MessageService,
    private encryptionService: EncryptionService
  ) {
    const alphaSpaces = /^[A-Za-z ]+$/;
    const mobile8to15 = /^[0-9]{8,15}$/;
    this.signInForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
    });

    this.signUpForm = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.minLength(2), Validators.pattern(alphaSpaces)]],
      lastName: ['', [Validators.required, Validators.minLength(2), Validators.pattern(alphaSpaces)]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(15), Validators.pattern(mobile8to15)]],
      password: ['', [Validators.required, this.strongPasswordValidator()]],
      confirmPassword: ['', [Validators.required]],
      investmentGoals: ['', [Validators.required]],
    });

    this.forgotPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit() {
    this.signUpForm.get('password')?.valueChanges.subscribe((val: string) => {
      const v = val || '';
      this.passwordCriteria = {
        length: v.length >= 8,
        upper: /[A-Z]/.test(v),
        lower: /[a-z]/.test(v),
        number: /[0-9]/.test(v),
        special: /[^A-Za-z0-9]/.test(v),
      };
      this.signUpForm.get('confirmPassword')?.updateValueAndValidity({ onlySelf: true });
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isLogin'] && !changes['isLogin'].firstChange) {
      this.updateValidators();
    }
  }

  toggleAuthMode() {
    this.isLogin = !this.isLogin;
    this.updateValidators();
  }

  private updateValidators() {
    const confirmPasswordControl = this.signUpForm.get('confirmPassword');
    confirmPasswordControl?.setValidators([Validators.required, this.matchOtherValidator('password')]);
    confirmPasswordControl?.updateValueAndValidity();
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onConfirmPasswordPaste(event: ClipboardEvent): void {
    event.preventDefault();
    this.messageService.add({
      severity: 'warn',
      summary: 'Paste Disabled',
      detail: 'Please type your password confirmation manually for security.',
      life: 2000
    });
  }

  private strongPasswordValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const v = (control.value || '') as string;
      if (!v) return null;
      const checks = [
        v.length >= 8,
        /[A-Z]/.test(v),
        /[a-z]/.test(v),
        /[0-9]/.test(v),
        /[^A-Za-z0-9]/.test(v),
      ];
      const ok = checks.every(Boolean);
      return ok ? null : { weakPassword: true };
    };
  }

  private matchOtherValidator(otherControlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control?.parent) return null;
      const other = control.parent.get(otherControlName);
      if (!other) return null;
      return control.value === other.value ? null : { mismatch: true };
    };
  }

  showForgotPasswordDialog() {
    this.forgotPasswordForm.reset();
    if (this.isLogin && this.signInForm.get('email')?.valid) {
      this.forgotPasswordForm.patchValue({
        email: this.signInForm.get('email')?.value
      });
    }
    this.showForgotPasswordVisisble = true;
  }

  closeForgotPasswordDialog() {
    this.showForgotPasswordVisisble = false;
  }

  onForgotPasswordSubmit() {
    if (this.forgotPasswordForm.invalid) {
      this.forgotPasswordForm.markAllAsTouched();
      return;
    }
    this.loading = true;
    const { email } = this.forgotPasswordForm.value;
    this.authService.forgotPassword(email).subscribe({
      next: (response) => {
        this.loading = false;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: response.message, life: 3000 });
        this.showForgotPasswordVisisble = false;
      },
      error: (err) => {
        this.loading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error.message, life: 3000 });
      }
    });
  }

  onSignIn() {
    if (this.signInForm.invalid) {
      this.signInForm.markAllAsTouched();
      return;
    }
    // Prepare encrypted payload
    let payload: any = { ...this.signInForm.value };
    try {
      const plainPassword = this.signInForm.value.password;
      const encryptedPassword = this.encryptionService.encryptPassword(plainPassword);
      payload.password = encryptedPassword;
    } catch (e: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Encryption Error',
        detail: e?.message || 'Failed to encrypt password. Please contact support.'
      });
      return;
    }

    this.loading = true;
    this.userService.signin(payload).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response.success === true) {
          this.messageService.add({
            severity: 'success',
            summary: 'Sign In Successful',
            detail: response.message || 'Welcome back!'
          });
          localStorage.setItem('authToken', response.data.tokens.authToken);
          localStorage.setItem('refreshToken', response.data.tokens.refreshToken);

          this.onLogin.emit(response.data.user);
          this.router.navigate(['/user']);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Sign In Failed',
            detail: response.message || 'Invalid credentials.'
          });
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Sign In Error',
          detail: error?.error?.message || 'An error occurred during sign in.'
        });
      }
    });
  }

  onSignUp() {
    if (this.signUpForm.invalid) {
      this.signUpForm.markAllAsTouched();
      return;
    }
    // Prepare encrypted payload
    let payload: any = { ...this.signUpForm.value };
    try {
      const plainPassword = this.signUpForm.value.password;
      const encryptedPassword = this.encryptionService.encryptPassword(plainPassword);
      payload.password = encryptedPassword;
      if (payload.confirmPassword !== undefined) {
        // Ensure confirm matches password on backend validation (deterministic encryption)
        payload.confirmPassword = encryptedPassword;
      }
    } catch (e: any) {
      this.messageService.add({
        severity: 'error',
        summary: 'Encryption Error',
        detail: e?.message || 'Failed to encrypt password. Please contact support.'
      });
      return;
    }

    this.loading = true;
    this.userService.signup(payload).subscribe({
      next: (response: any) => {
        this.loading = false;
        if (response.success === true) {
          // Close signup modal and clear the form, then navigate to styled view
          this.signUpForm.reset();
          this.closeModal();
          this.isLogin = true;
          this.router.navigate(['/verify-email-sent']);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Sign Up Failed',
            detail: response.message || 'Unable to create account.'
          });
        }
      },
      error: (error: any) => {
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Sign Up Error',
          detail: error?.error?.message || 'An error occurred during sign up.'
        });
      }
    });
  }

  closeModal() {
    this.onClose.emit();
  }
}
